import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { NavigationEnd, Router } from '@angular/router';
import { debounceTime, filter, Subject, takeUntil } from 'rxjs';

import { PAGES_ROUTES } from '@efficiently/client-core-routing';
import { SIDE_EFFECT_DEBOUNCE_MS } from '@efficiently/client-ui-common';

import { AiAssistantService } from './ai-assistant';

@Component({
	selector: 'ds-assistant',
	templateUrl: './design-assistant.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [ReactiveFormsModule, MatSlideToggle],
})
export class DesignAssistantComponent implements OnInit, OnDestroy {
	private readonly aiAssistantService = inject(AiAssistantService);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly router = inject(Router);

	private destroy$: Subject<void> = new Subject<void>();
	public activeControl = new FormControl<boolean>(false, { nonNullable: true });
	public initStream: boolean = false;
	private wasOnProjectPage: boolean = false;

	public ngOnInit(): void {
		this.activeControl.valueChanges
			.pipe(debounceTime(SIDE_EFFECT_DEBOUNCE_MS), takeUntil(this.destroy$))
			.subscribe(active => {
				if (active) {
					this.initAssistant();
				} else {
					this.disableAssistant();
				}
			});

		this.router.events
			.pipe(
				filter((event): event is NavigationEnd => event instanceof NavigationEnd),
				takeUntil(this.destroy$),
			)
			.subscribe(event => {
				const isProjectPage = this.isProjectRoute(event.url);

				// If user enters project page and assistant is active
				if (isProjectPage && !this.wasOnProjectPage && this.activeControl.value) {
					this.reinitAssistant();
				}

				// If user leaves project page and assistant is active
				if (!isProjectPage && this.wasOnProjectPage && this.activeControl.value) {
					this.reinitAssistant();
				}

				// Update state
				this.wasOnProjectPage = isProjectPage;
			});
		this.wasOnProjectPage = this.isProjectRoute(this.router.url);
	}

	private isProjectRoute(url: string): boolean {
		// Check if URL matches project routes
		return url.includes(`/${PAGES_ROUTES.PROJECTS.path}/`) && !url.includes(`/${PAGES_ROUTES.PROJECTS.path}/list`);
	}

	private async reinitAssistant(): Promise<void> {
		// Reinitialize assistant when navigating to/from project page
		await this.disableAssistant();
		this.aiAssistantService.clearMemories();
		await this.initAssistant(true);
	}

	private async initAssistant(reinit: boolean = false): Promise<void> {
		await this.aiAssistantService.initializeVoiceConnection(reinit);
		this.initStream = true;
		this.cdr.detectChanges();
	}

	private async disableAssistant(): Promise<void> {
		this.initStream = false;
		this.aiAssistantService.disconnect();
		this.cdr.detectChanges();
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
